(() => {
  'use strict';

  const form = document.getElementById('builderForm');
  const statusNode = document.getElementById('builderStatus');
  const usageNode = document.getElementById('builderUsage');
  const resultNode = document.getElementById('builderResult');
  const savedPreview = document.getElementById('builderSavedPreview');
  const projectSearchForm = document.getElementById('projectSearchForm');
  const projectSearchStatus = document.getElementById('projectSearchStatus');
  const projectBrowserResults = document.getElementById('projectBrowserResults');
  const crashButton = document.getElementById('analyzeCrashLog');
  const sourceSafetyForm = document.getElementById('sourceSafetyForm');
  const sourceSafetyResult = document.getElementById('sourceSafetyResult');

  let currentProfile = null;
  let currentProfileCode = '';
  let selectedNames = [];

  const starterIdeas = {
    'low-end-performance': {
      name: 'Low-End Survival Performance Pack',
      prompt: 'Create a survival modpack for low-end PCs with performance fixes, stable quality-of-life mods, safe worldgen choices, and no heavy shader requirement.',
      specificMods: 'Sodium, Lithium, FerriteCore, ModernFix, Entity Culling, AppleSkin, JourneyMap',
      vibe: 'stable, lightweight, low-end friendly, vanilla plus',
      version: '1.21.1',
      loader: 'fabric',
      modCount: '32',
      includeShaders: false,
      includeResourcePacks: true
    },
    'vanilla-friendly-building': {
      name: 'Vanilla Friendly Building Pack',
      prompt: 'Create a vanilla-friendly building and decoration pack with better blocks, furniture, storage, maps, performance support, and light survival improvements.',
      specificMods: 'Chipped, Supplementaries, Decorative Blocks, Macaw Furniture, Storage Drawers, Xaero Minimap',
      vibe: 'builder friendly, cozy, vanilla plus',
      version: '1.20.1',
      loader: 'forge',
      modCount: '42',
      includeShaders: false,
      includeResourcePacks: true
    },
    'adventure-rpg-exploration': {
      name: 'Adventure RPG Exploration Pack',
      prompt: 'Create an adventure RPG pack with structures, bosses, loot, travel tools, better villages, maps, and performance support.',
      specificMods: 'Waystones, YUNG Better Dungeons, When Dungeons Arise, Artifacts, Better Combat, JourneyMap',
      vibe: 'exploration, RPG, bosses, long-term survival',
      version: '1.20.1',
      loader: 'forge',
      modCount: '55',
      includeShaders: false,
      includeResourcePacks: true
    },
    'shaders-resource-packs': {
      name: 'Visual Upgrade Pack',
      prompt: 'Create a visual improvement pack with shader support, resource packs, entity texture features, performance support, and stable client-side utilities.',
      specificMods: 'Sodium, Iris, Entity Texture Features, Entity Model Features, Continuity, Fabric API',
      vibe: 'visual polish, performance, client-side',
      version: '1.21.1',
      loader: 'fabric',
      modCount: '24',
      includeShaders: true,
      includeResourcePacks: true
    }
  };

  const escapeHTML = value => String(value ?? '').replace(/[&<>"']/g, character => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[character]);

  function setStatus(message, tone = '') {
    if (!statusNode) return;
    statusNode.textContent = message;
    statusNode.className = `form-note ${tone}`.trim();
  }

  function setBusy(isBusy) {
    document.body.classList.toggle('builder-is-generating', isBusy);
    form?.querySelectorAll('input,select,textarea,button').forEach(control => {
      if (control.id !== 'randomizePack') control.disabled = isBusy;
    });
    const submit = form?.querySelector('[type="submit"]');
    if (submit) submit.textContent = isBusy ? 'Generating pack...' : 'Generate CurseForge profile';
  }

  function decodeProfile(code) {
    try {
      const raw = String(code || '').replace(/-/g, '+').replace(/_/g, '/');
      const pad = raw.length % 4 ? '='.repeat(4 - raw.length % 4) : '';
      const bin = atob(raw + pad);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch {
      return null;
    }
  }

  function projectsFrom(profile, code = '') {
    const decoded = profile?.fmm?.projects?.length ? null : decodeProfile(code);
    const source = profile?.fmm?.projects?.length ? profile : (decoded || profile || {});
    const projects = Array.isArray(source?.fmm?.projects) ? source.fmm.projects : [];
    const files = Array.isArray(source?.files) ? source.files : [];
    if (projects.length) return projects;
    return files.map(file => ({
      projectID: file.projectID,
      fileID: file.fileID,
      name: `CurseForge project ${file.projectID}`,
      type: 'mod',
      reason: 'Included in generated manifest'
    }));
  }

  async function loadStatus() {
    if (!usageNode) return;
    usageNode.textContent = 'Checking free builder status...';
    try {
      const response = await fetch('/api/modpack-builder/status', { headers: { accept: 'application/json' } });
      const data = await response.json().catch(() => ({}));
      if (data.setup_required) {
        usageNode.innerHTML = `<span class="warn-inline">${escapeHTML(data.message || 'Builder setup is not finished yet.')}</span>`;
        return;
      }
      const cf = data.curseForgeConfigured ? 'CurseForge manifest export ready' : 'CurseForge API setup needed for live project manifests';
      const ai = data.aiConfigured ? 'free-light AI enabled' : 'rules-first tools enabled';
      usageNode.innerHTML = `<strong>Free builder</strong> - ${escapeHTML(cf)} - ${escapeHTML(ai)}. Sign in only to save or publish packs.`;
    } catch {
      usageNode.textContent = 'Free builder status could not be checked yet.';
    }
  }

  async function loadSavedPreview() {
    if (!savedPreview) return;
    try {
      const response = await fetch('/api/modpack-builder/saved', { headers: { accept: 'application/json' } });
      if (response.status === 401) {
        savedPreview.innerHTML = '<p class="muted">Sign in to see saved packs here. Generation and export still work without signing in.</p>';
        return;
      }
      const data = await response.json().catch(() => ({}));
      const items = (data.items || []).slice(0, 4);
      savedPreview.innerHTML = items.length
        ? `<h3>Recently saved</h3><div class="mini-saved-pack-list">${items.map(item => `<a href="/saved-modpacks" class="mini-saved-pack"><strong>${escapeHTML(item.name || 'Saved Modpack')}</strong><span>${escapeHTML(item.project_count || 0)} projects - ${escapeHTML(item.loader || 'Any loader')} - ${escapeHTML(item.minecraft_version || 'Any version')}</span></a>`).join('')}</div>`
        : '<p class="muted">No saved packs yet. Generate a pack, then sign in to save it.</p>';
    } catch {
      savedPreview.innerHTML = '<p class="muted">Saved packs are available after sign-in.</p>';
    }
  }

  function addProjectName(name) {
    const clean = String(name || '').trim();
    if (!clean || selectedNames.includes(clean)) return;
    selectedNames.push(clean);
    const input = form?.elements.specificMods;
    if (input) {
      const current = String(input.value || '').split(',').map(value => value.trim()).filter(Boolean);
      if (!current.some(value => value.toLowerCase() === clean.toLowerCase())) current.push(clean);
      input.value = current.join(', ');
    }
    setStatus(`Added ${clean} to the requested project list.`, 'good');
  }

  function renderProjectSearch(items = []) {
    if (!projectBrowserResults) return;
    projectBrowserResults.innerHTML = items.length ? items.map(item => {
      const title = item.name || item.title || item.id || 'Project';
      const icon = item.icon ? `<img src="${escapeHTML(item.icon)}" alt="" width="48" height="48" loading="lazy" decoding="async" onerror="this.hidden=true;this.nextElementSibling.hidden=false">` : '';
      const fallback = `<span class="project-logo-fallback builder-project-fallback"${icon ? ' hidden' : ''} aria-hidden="true">${escapeHTML(title.slice(0, 2).toUpperCase())}</span>`;
      return `<article class="builder-browser-item">
      <span class="builder-browser-media">${icon}${fallback}</span>
      <div><strong>${escapeHTML(item.name)}</strong><p>${escapeHTML(item.summary || 'Official CurseForge project result.').slice(0, 180)}</p><small>${escapeHTML(item.type || 'mod')} - ${escapeHTML(item.source || 'CurseForge')} - ${escapeHTML(item.version || 'selected version')}</small></div>
      <button class="btn ghost compact" type="button" data-add-project="${escapeHTML(item.name)}">Add</button>
    </article>`;
    }).join('') : '<p class="muted-note">No compatible projects were found for that search. Try a broader name or another version/loader.</p>';
    projectBrowserResults.querySelectorAll('[data-add-project]').forEach(button => {
      button.addEventListener('click', () => addProjectName(button.dataset.addProject));
    });
  }

  projectSearchForm?.addEventListener('submit', async event => {
    event.preventDefault();
    const data = new FormData(projectSearchForm);
    const version = form?.elements.version?.value || data.get('version') || '1.21.1';
    const loader = form?.elements.loader?.value || data.get('loader') || 'any';
    const params = new URLSearchParams({
      q: String(data.get('q') || ''),
      type: String(data.get('type') || 'mod'),
      version,
      loader,
      limit: '18'
    });
    projectSearchStatus.textContent = 'Searching official CurseForge projects...';
    try {
      const response = await fetch(`/api/modpack-builder/project-browser?${params}`, { headers: { accept: 'application/json' } });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message || 'Project search failed.');
      renderProjectSearch(payload.items || []);
      projectSearchStatus.textContent = `${(payload.items || []).length} compatible result${(payload.items || []).length === 1 ? '' : 's'} found.`;
    } catch (error) {
      projectBrowserResults.innerHTML = `<p class="muted-note">${escapeHTML(error.message || 'Project search is unavailable right now.')}</p>`;
      projectSearchStatus.textContent = '';
    }
  });

  async function downloadZip() {
    if (!currentProfile && !currentProfileCode) return;
    setStatus('Preparing CurseForge ZIP...');
    const response = await fetch('/api/modpack-builder/export', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ profile: currentProfile, profileCode: currentProfileCode })
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      setStatus(data.message || 'Could not export ZIP.', 'warn');
      return;
    }
    const blob = await response.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${String(currentProfile?.name || 'fmm-modpack').replace(/[^a-z0-9_-]+/gi, '-')}.zip`;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(link.href);
      link.remove();
    }, 2000);
    setStatus('ZIP downloaded. Import it in the CurseForge app.', 'good');
  }

  async function savePack() {
    if (!currentProfile) return;
    setStatus('Saving pack to your account...');
    const response = await fetch('/api/modpack-builder/saved', {
      method: 'POST',
      headers: { 'content-type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        profile: currentProfile,
        profileCode: currentProfileCode,
        name: currentProfile.name,
        description: currentProfile.fmm?.summary || '',
        projects: projectsFrom(currentProfile, currentProfileCode)
      })
    });
    if (response.status === 401) {
      setStatus('Sign in to save this pack. Generation and ZIP export are already available.', 'warn');
      resultNode?.querySelector('.builder-actions')?.insertAdjacentHTML('beforeend', '<a class="btn ghost" href="/account?next=/modpack-builder">Sign in to save</a>');
      return;
    }
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      setStatus(data.message || 'Could not save pack.', 'warn');
      return;
    }
    setStatus(data.message || 'Saved modpack.', 'good');
    await loadSavedPreview();
  }

  function noteList(title, notes = [], emptyText = '') {
    const clean = notes.filter(Boolean).slice(0, 12);
    if (!clean.length) return `<div class="builder-note-card"><strong>${escapeHTML(title)}</strong><p>${escapeHTML(emptyText || 'No warnings were reported.')}</p></div>`;
    return `<div class="builder-note-card"><strong>${escapeHTML(title)}</strong><ul>${clean.map(note => `<li>${escapeHTML(note)}</li>`).join('')}</ul></div>`;
  }

  function renderProfile(profile, profileCode, meta = {}) {
    currentProfile = profile;
    currentProfileCode = profileCode;
    const projects = projectsFrom(profile, profileCode);
    const fmm = profile.fmm || {};
    const compatibility = fmm.compatibilityScan || {};
    const dependency = fmm.dependencyScan || {};
    const breakdown = Object.entries(fmm.projectBreakdown || {}).map(([key, value]) => `${escapeHTML(key)}: ${escapeHTML(value)}`).join(' - ');
    const cards = projects.map(project => `<a class="builder-mod-item" href="${escapeHTML(project.url || '#')}" target="_blank" rel="noopener noreferrer">
      ${project.icon ? `<img src="${escapeHTML(project.icon)}" alt="" width="48" height="48" loading="lazy" decoding="async">` : '<span class="builder-mod-fallback">P</span>'}
      <span><strong>${escapeHTML(project.name || project.title || project.projectID || 'Minecraft project')}</strong><br><small>${escapeHTML(project.type || 'mod')} - ${escapeHTML(project.reason || 'Included in generated profile')}</small></span>
    </a>`).join('');

    resultNode.innerHTML = `<p class="eyebrow">Profile ready</p><h2>${escapeHTML(profile.name || 'Generated Modpack')}</h2>
      <p>${escapeHTML(fmm.summary || 'Generated CurseForge modpack profile.')}</p>
      <div class="usage-meter">Free generation complete - ${escapeHTML(projects.length)} compatible project${projects.length === 1 ? '' : 's'}${breakdown ? ` - ${breakdown}` : ''}${meta.usedAi ? ' - AI-assisted search plan' : ' - rules-first search plan'}</div>
      <div class="builder-actions"><button class="btn primary" id="downloadPack" type="button">Download CurseForge ZIP</button><button class="btn ghost" id="savePack" type="button">Save pack</button><button class="btn ghost" id="copyCode" type="button">Copy profile code</button></div>
      <label class="builder-code-label">Profile code<textarea class="builder-code" readonly>${escapeHTML(profileCode)}</textarea></label>
      <div class="builder-review-grid">${noteList('Compatibility review', compatibility.notes || [], compatibility.summary || 'No obvious requested-loader conflicts were detected.')}${noteList('Dependency review', dependency.notes || [], dependency.summary || 'No missing required CurseForge dependencies were detected.')}</div>
      <div class="builder-preview-head"><h3>Included projects</h3><span>${escapeHTML(projects.length)} projects</span></div><div class="builder-mod-list">${cards || '<p class="muted-note">The manifest was generated, but readable project details were unavailable.</p>'}</div>`;
    document.getElementById('downloadPack')?.addEventListener('click', downloadZip);
    document.getElementById('savePack')?.addEventListener('click', savePack);
    document.getElementById('copyCode')?.addEventListener('click', async () => {
      await navigator.clipboard.writeText(profileCode);
      setStatus('Profile code copied.', 'good');
    });
    resultNode.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  form?.addEventListener('submit', async event => {
    event.preventDefault();
    const data = new FormData(form);
    const payload = Object.fromEntries(data.entries());
    payload.name = String(payload.name || '').trim() || 'FreeMinecraftMods Pack';
    payload.prompt = String(payload.prompt || '').trim();
    payload.specificMods = String(payload.specificMods || '').trim();
    payload.vibe = String(payload.vibe || '').trim();
    payload.version = String(payload.version || '1.21.1').trim();
    payload.loader = String(payload.loader || 'any').trim().toLowerCase();
    payload.includeShaders = data.has('includeShaders');
    payload.includeResourcePacks = data.has('includeResourcePacks');
    payload.modCount = Math.max(10, Math.min(Number(payload.modCount || 80), 500));
    if (form.dataset.randomSeed) payload.randomSeed = form.dataset.randomSeed;
    setBusy(true);
    setStatus(`Generating ${payload.name}...`);
    resultNode.innerHTML = '<div class="builder-generating-card"><div class="builder-spinner" aria-hidden="true"></div><div><p class="eyebrow">Building your pack</p><h2>Generating a compatible modpack...</h2><p>The builder is searching official projects, checking loader/version fit, adding required APIs, and cleaning up obvious conflicts.</p><div class="builder-progress"><span></span></div></div></div>';
    try {
      const response = await fetch('/api/modpack-builder/build', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json().catch(() => ({}));
      if (result.setup_required) {
        setStatus(result.message || 'Builder setup is not finished yet.', 'warn');
        resultNode.innerHTML = `<div class="muted-note">${escapeHTML(result.message || 'Builder setup is not finished yet.')}</div>`;
        return;
      }
      if (!response.ok || !result.profile) {
        setStatus(result.message || 'Could not generate a modpack yet.', 'warn');
        resultNode.innerHTML = `<div class="muted-note">${escapeHTML(result.message || 'The builder could not generate a compatible pack for that request.')}</div>`;
        return;
      }
      renderProfile(result.profile, result.profileCode, result);
      setStatus('Generated successfully.', 'good');
      await loadStatus();
    } catch {
      setStatus('The builder could not reach the backend yet.', 'warn');
      resultNode.innerHTML = '<div class="muted-note">The backend could not be reached. Try again after the site finishes deploying.</div>';
    } finally {
      setBusy(false);
    }
  });

  document.getElementById('randomizePack')?.addEventListener('click', () => {
    if (!form) return;
    const names = ['Copper Frontier', 'Verdant Odyssey', 'Astral Workshop', 'Sunken Wilds', 'Ancient Foundry', 'Crystal Reaches', 'Mossbound Realms'];
    const themes = [
      'balanced survival, exploration, structures, maps, storage, and performance support',
      'cozy building, farming, villages, decoration, and lightweight quality-of-life features',
      'automation, storage, logistics, Create-style progression, and server-friendly utilities',
      'adventure RPG combat, bosses, dungeons, travel, maps, and loot progression'
    ];
    const pick = list => list[Math.floor(Math.random() * list.length)];
    form.elements.name.value = pick(names);
    form.elements.prompt.value = `Build a cohesive Minecraft pack around ${pick(themes)}. Keep the project list compatible with the selected loader and version.`;
    form.elements.vibe.value = 'cohesive, replayable, source-safe, compatibility checked';
    form.elements.specificMods.value = '';
    form.dataset.randomSeed = String(Date.now());
    form.requestSubmit();
  });

  crashButton?.addEventListener('click', async () => {
    const input = document.getElementById('crashLogInput');
    const status = document.getElementById('crashLogStatus');
    const output = document.getElementById('crashLogResult');
    if (!input || !status || !output) return;
    status.textContent = 'Analyzing crash log...';
    output.innerHTML = '';
    try {
      const response = await fetch('/api/modpack-builder/crash-log', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ log: input.value })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Crash log analysis failed.');
      const analysis = data.analysis || {};
      status.textContent = data.message || 'Crash log analyzed.';
      output.innerHTML = `<div class="builder-note-card"><h3>${escapeHTML(analysis.summary || 'Crash log review')}</h3><ul>${(analysis.fixes || []).map(fix => `<li>${escapeHTML(fix)}</li>`).join('')}</ul>${analysis.remove?.length ? `<p><strong>Suggested removals:</strong> ${analysis.remove.map(escapeHTML).join(', ')}</p>` : ''}${analysis.add?.length ? `<p><strong>Suggested additions:</strong> ${analysis.add.map(escapeHTML).join(', ')}</p>` : ''}</div>`;
    } catch (error) {
      status.textContent = error.message || 'Crash log analysis is unavailable.';
    }
  });

  sourceSafetyForm?.addEventListener('submit', event => {
    event.preventDefault();
    const value = String(new FormData(sourceSafetyForm).get('source') || '').trim();
    const official = /(curseforge\.com|modrinth\.com|github\.com|planetminecraft\.com|hangar\.papermc\.io|spigotmc\.org|papermc\.io)/i.test(value);
    const risky = /(free\s*download|mediafire|adf\.ly|linkvertise|shortener|exe|password|required|launcher)/i.test(value);
    const notes = [];
    notes.push(official ? 'Recognized official or creator-oriented source domain.' : 'Use extra caution if this is not an official project, creator, platform, or repository page.');
    notes.push(risky ? 'The text includes terms often seen around risky mirrors or deceptive download flows.' : 'No obvious risky mirror terms were detected.');
    notes.push('Prefer pages with visible creator names, version history, changelogs, files, dependencies, and community reports.');
    sourceSafetyResult.innerHTML = `<div class="builder-note-card ${risky ? 'warn' : 'good'}"><h3>${official && !risky ? 'Looks source-safe' : 'Needs review'}</h3><ul>${notes.map(note => `<li>${escapeHTML(note)}</li>`).join('')}</ul></div>`;
  });

  function applyStarterIdea() {
    const params = new URLSearchParams(location.search);
    let idea = params.get('idea') || '';
    try {
      idea ||= localStorage.getItem('fmmPendingModpackIdea') || '';
      localStorage.removeItem('fmmPendingModpackIdea');
    } catch {}
    const starter = starterIdeas[idea];
    if (!starter || !form) return;
    for (const [name, value] of Object.entries(starter)) {
      const control = form.elements[name];
      if (!control) continue;
      if (control.type === 'checkbox') control.checked = Boolean(value);
      else control.value = String(value);
    }
    setStatus(`Loaded starter idea: ${starter.name}. Review it, then generate the pack.`, 'good');
  }

  loadStatus();
  loadSavedPreview();
  applyStarterIdea();
})();
